import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Badge from 'components/Badge';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { AppContext } from 'shared/AppContext';
import { useGetTrackEvents } from './gql/get-track-events';
import useEventsExport from './hooks/useEventsExport';

const TrackEvents = () => {
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  // Filter state - pending (shown in filter panel) vs applied (used in query)
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  const { pathname } = useLocation();
  const userType = pathname.split('/').includes('admin-employee')
    ? 'employee'
    : 'track';
  const routePrefix = `/admin-${userType}`;

  const appContext = useContext(AppContext);
  const accountId = appContext?.state?.accountId;

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Fetch events data with date filters
  const { data, loading } = useGetTrackEvents(appliedStartDate, appliedEndDate);

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useEventsExport();

  useEffect(() => {
    if (data?.getEventsByAccount) {
      setEvents(data.getEventsByAccount.results || []);
      setCount(data.getEventsByAccount.count || 0);
    }
  }, [data]);

  // Handle search - only reset page when user actively searches, not on mount
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle filter change - updates pending state
  const handleFilterChange = (newFilters) => {
    if (newFilters.startDate !== undefined) {
      setPendingStartDate(newFilters.startDate || '');
    }
    if (newFilters.endDate !== undefined) {
      setPendingEndDate(newFilters.endDate || '');
    }
  };

  // Handle filter apply - updates applied state to trigger query
  const handleApplyFilters = (newFilters) => {
    const newStartDate = newFilters.startDate ?? pendingStartDate;
    const newEndDate = newFilters.endDate ?? pendingEndDate;

    setAppliedStartDate(newStartDate);
    setAppliedEndDate(newEndDate);
    setPendingStartDate(newStartDate);
    setPendingEndDate(newEndDate);

    updateQuery({ page: 1 });
  };

  // Handle filter clear
  const handleClearFilters = () => {
    setAppliedStartDate('');
    setAppliedEndDate('');
    setPendingStartDate('');
    setPendingEndDate('');
    updateQuery({ page: 1 });
  };

  // Build filter configuration for TableFilter
  const filterConfig = [
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date',
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'date',
    },
  ];

  // Current filter values for TableFilter
  const filterValues = {
    startDate: pendingStartDate,
    endDate: pendingEndDate,
  };

  // Handle export with selected format
  const handleExport = useCallback(
    async (format) => {
      // Close modal
      setShowExportModal(false);

      // Show processing toaster
      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        // Build filters object
        const filters = {
          ...(input.queryString && { search: input.queryString }),
          ...(appliedStartDate && { start_date: appliedStartDate }),
          ...(appliedEndDate && { end_date: appliedEndDate }),
        };

        // Start export mutation with format
        const jobId = await startExport(accountId, filters, format);

        // Subscribe to progress updates
        subscribeToProgress(jobId, {
          onProgress: (status) => {
            // Update toaster with progress
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} events (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              // Get download URL
              const downloadUrl = await getDownloadUrl(jobId);

              // Show success toaster with download link
              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } events exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            // Show error toaster
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export from this account';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        // Show error toaster for mutation failure
        setToasterData({
          title: 'Export Failed',
          message: 'Unable to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      accountId,
      input.queryString,
      appliedStartDate,
      appliedEndDate,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'published':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'draft':
        return 'warning';
      case 'postponed':
        return 'warning';
      case 'complete':
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  // Check if we have multiple tracks in the data
  const showTracks = events.reduce((acc, event) => {
    if (acc === true) return acc;
    if (acc === false) return event.track?.id;
    return acc !== event.track?.id ? true : event.track?.id;
  }, false);

  // DataTable columns configuration
  const columns = [
    {
      field: 'date',
      header: 'Date',
      mobileSubtitle: true, // Show as subtitle on mobile collapsed view
      render: (event) => {
        const { isMultiDay, day, date, month, listDates, year } = event;
        return isMultiDay
          ? `${month?.toUpperCase()} ${listDates} - ${year}`
          : `${day} ${date} - ${year}`;
      },
    },
    {
      field: 'name',
      header: 'Event',
      mobileTitle: true, // Use as title on mobile collapsed view
      render: (item) => <CellPrimary>{item.name}</CellPrimary>,
    },
    ...(showTracks === true
      ? [
          {
            field: 'track',
            header: 'Track',
            render: (event) => event.track?.name || '',
          },
        ]
      : []),
    {
      field: 'sale_start',
      header: 'On Sale Date',
      render: (event) => {
        const saleDate = event.sale_start || event.registration_sale_start;
        if (!saleDate) return '';
        return moment(saleDate, 'MM-DD-YYYY')
          .format('ddd MMM D - YYYY')
          .toUpperCase();
      },
    },
    {
      field: 'status',
      header: 'Status',
      render: (event) => (
        <Badge variant={getStatusVariant(event.status)}>
          {event.status
            ? event.status.charAt(0).toUpperCase() +
              event.status.slice(1).toLowerCase()
            : 'N/A'}
        </Badge>
      ),
    },
  ];

  const totalPages = Math.ceil(count / 15);

  // Actions for DataTable
  const actions = [
    {
      type: 'view',
      title: 'View Event',
      mobileLabel: 'View Event',
      to: (item) => ({
        pathname: `${routePrefix}/events/menu/${item.id}`,
        state: { event: item },
      }),
    },
  ];

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="events" />
            <HeaderTitle>Events</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Page Header with Search and Date Filter */}
                <PageToolbar>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search by Event Name..."
                    debounceMs={500}
                  />
                  <TableFilter
                    filters={filterConfig}
                    values={filterValues}
                    onChange={handleFilterChange}
                    onApply={handleApplyFilters}
                    onClear={handleClearFilters}
                  />
                </PageToolbar>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={events}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No events found"
                  emptySubtext="Try adjusting your search or filters"
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          recordCount={count || 0}
          dataType="events"
        />

        {/* Toaster */}
        {toasterData && (
          <Toaster
            isOpen={!!toasterData}
            onClose={() => setToasterData(null)}
            title={toasterData.title}
            message={toasterData.message}
            type={toasterData.type}
            duration={toasterData.persistent ? 25000 : 10000}
            persistent={toasterData.persistent}
            action={toasterData.action}
          />
        )}
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackEvents;
